import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Configure, InstantSearch } from 'react-instantsearch-dom';
import algoliasearch from 'algoliasearch/lite';
import themeColors from '../../../assets/styles/themeColors';
import Modal from '../../Modal/Modal';
import config from '../../../config';
import { SFH_WIZARD } from '../../../constants/sessionStorage';
import { accountTransactionsFetch } from '../../../store/actions/portfolio/portfolioActions';
import { fetchAccountAgreement } from '../../../store/actions/account/accountActions';
import { SELL_FROM_HOME_AGREEMENT_TYPE } from '../../../constants/agreementConstants';
import { RECENT_TRANSACTIONS } from '../../../constants/accountTransactionConstants';
import {
  getCurrentAccountUid,
  selectIsSellFromHomeRestricted,
} from '../../../store/selectors/accountSelectors';
import {
  closeWizardContent,
  getWizardContent,
  setWizardContent,
} from '../../../util/helpers/wizardHelpers';
import SellFromHomeProductSelection from './SellFromHomeProductSelection';
import SellFromHomeQuantity from './SellFromHomeQuantity';
import SellFromHomeShipping from './SellFromHomeShipping';
import SellFromHomeReview from './SellFromHomeReview';
import SellFromHomeConfirmation from './SellFromHomeConfirmation';
import SellFromHomeTransactionDetails from './SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from './SellFromHomeTransactionHistory';
import SellFromHomeStepsDrawer from './SellFromHomeStepsDrawer';
import SellFromHomeLockedModal from './SellFromHomeLockedModal';
import SfhModalTitle from './SfhModalTitle';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_TOP_BACK_CLICKED,
  SFH_TOP_CLOSE_CLICKED,
} from '../../../constants/gtmEvents';

const { apiKey, apiSecret, apiSfhIndex } = config.algoliaSearch;
const searchClient = algoliasearch(apiKey, apiSecret);

// ─── SFH wizard dimensions ────────────────────────────────────────────────────
const SFH_WIDTH = '600px';
const SFH_HEIGHT = '620px';
const SFH_REVIEW_HEIGHT = '680px';
// ─────────────────────────────────────────────────────────────────────────────

const PRODUCT_SELECTION_STEP = 0;
const QUANTITY_STEP = 1;
const SHIPPING_STEP = 2;
const REVIEW_STEP = 3;
const CONFIRMATION_STEP = 4;
const TRANSACTION_DETAILS_STEP = 5;
const TRANSACTION_HISTORY_STEP = 6;

const STEP_TITLE_KEYS = {
  [PRODUCT_SELECTION_STEP]: 'sellWizard.sfhWizard.title',
  [QUANTITY_STEP]: 'sellWizard.sfhWizard.title',
  [SHIPPING_STEP]: 'sellWizard.sfhWizard.title',
  [REVIEW_STEP]: 'sellWizard.sfhWizard.review.title',
  [CONFIRMATION_STEP]: null,
  [TRANSACTION_DETAILS_STEP]: 'sellWizard.sfhWizard.transactionDetails.title',
  [TRANSACTION_HISTORY_STEP]: 'sellWizard.sfhWizard.transactionHistory.title',
};

const STEP_NAMES = {
  [PRODUCT_SELECTION_STEP]: 'sku_selection',
  [QUANTITY_STEP]: 'quantity_selection',
  [SHIPPING_STEP]: 'shipping',
  [REVIEW_STEP]: 'review',
  [CONFIRMATION_STEP]: 'confirmation',
  [TRANSACTION_DETAILS_STEP]: 'transaction_details',
  [TRANSACTION_HISTORY_STEP]: 'transaction_history',
};

const STEP_FIXED_HEIGHTS = {
  [PRODUCT_SELECTION_STEP]: SFH_HEIGHT,
  [QUANTITY_STEP]: SFH_HEIGHT,
  [SHIPPING_STEP]: SFH_HEIGHT,
  [REVIEW_STEP]: SFH_REVIEW_HEIGHT,
  [CONFIRMATION_STEP]: SFH_HEIGHT,
  [TRANSACTION_DETAILS_STEP]: SFH_HEIGHT,
  [TRANSACTION_HISTORY_STEP]: SFH_HEIGHT,
};

const SellFromHomeWizard = ({ isOpen, onClose, fromSellWizard }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sfhGtmEvent } = useSfhGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const isSellFromHomeRestricted = useSelector(selectIsSellFromHomeRestricted);
  const orderSubmittedRef = useRef(false);
  const closedForRestrictionRef = useRef(false);
  const [isStepsDrawerOpen, setIsStepsDrawerOpen] = useState(false);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const [selectedHit, setSelectedHit] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedOriginAddress, setSelectedOriginAddress] = useState(null);
  const [initialQuote, setInitialQuote] = useState(null);
  const [lockedSnapshot, setLockedSnapshot] = useState(null);
  // Fetched once per wizard-open (not once per Review-step mount) so
  // navigating back/forward between steps doesn't re-fetch the T&C content on
  // every visit to Review — see the fetch effect below. Reset on close so a
  // new order always gets a fresh fetch. T&C is informational only.
  const [termsPdfUrl, setTermsPdfUrl] = useState(null);
  const [, setIsTermsContentLoading] = useState(false);
  // High-value shipping notice checkbox — persists while the wizard stays open
  // (including closing/reopening the drawer) but resets on wizard close.
  const [shippingNoticeAcknowledged, setShippingNoticeAcknowledged] = useState(
    false,
  );
  const [currentStep, setCurrentStep] = useState(
    () => Number(getWizardContent(SFH_WIZARD)) || PRODUCT_SELECTION_STEP,
  );

  // Lets the Shipping step intercept the header back chevron (B2 -> B1) before
  // it falls through to the wizard-level step navigation.
  const shippingBackRef = useRef(null);

  useEffect(() => {
    setIsStepsDrawerOpen(isOpen);
    if (!isOpen) {
      setSelectedHit(null);
      setQuantity(0);
      setSelectedPaymentMethod(null);
      setSelectedOriginAddress(null);
      setInitialQuote(null);
      setLockedSnapshot(null);
      setTermsPdfUrl(null);
      setIsTermsContentLoading(false);
      setShippingNoticeAcknowledged(false);
      setCurrentStep(PRODUCT_SELECTION_STEP);
      orderSubmittedRef.current = false;
      closedForRestrictionRef.current = false;
      closeWizardContent();
    }
  }, [isOpen]);

  // Fetched once per wizard-open rather than once per Review-step mount, so
  // back/forward navigation between steps doesn't re-fetch on every visit to
  // Review. Still re-fetched fresh every time the wizard opens (a new order).
  useEffect(() => {
    if (!isOpen || !accountUid) return;
    dispatch(
      fetchAccountAgreement({
        accountUid,
        agreementType: SELL_FROM_HOME_AGREEMENT_TYPE,
        agreementFetchSuccess: (data) => {
          if (data) setTermsPdfUrl(URL.createObjectURL(data));
        },
        setIsLoading: setIsTermsContentLoading,
      }),
    );
  }, [isOpen, accountUid, dispatch]);

  // Object URLs aren't garbage collected automatically; revoke the previous
  // one whenever it's replaced or this component unmounts.
  useEffect(() => {
    if (!termsPdfUrl) return undefined;
    return () => URL.revokeObjectURL(termsPdfUrl);
  }, [termsPdfUrl]);

  useEffect(() => {
    setQuantity(0);
  }, [selectedHit?.Code]);

  const goToStep = (step) => {
    setWizardContent(SFH_WIZARD, step);
    setCurrentStep(step);
  };

  const goStepBack = () => {
    if (currentStep === QUANTITY_STEP) {
      setQuantity(0);
    }
    goToStep(Math.max(currentStep - 1, 0));
  };
  const goStepForward = () =>
    goToStep(Math.min(currentStep + 1, TRANSACTION_HISTORY_STEP));

  const refreshRecentTransactions = () => {
    if (!accountUid) return;
    dispatch(
      accountTransactionsFetch({
        accountUid,
        content: RECENT_TRANSACTIONS,
      }),
    );
  };

  const handleClose = () => {
    if (orderSubmittedRef.current) refreshRecentTransactions();
    orderSubmittedRef.current = false;
    closeWizardContent();
    setSelectedHit(null);
    setQuantity(0);
    setSelectedPaymentMethod(null);
    setSelectedOriginAddress(null);
    setInitialQuote(null);
    setLockedSnapshot(null);
    setTermsPdfUrl(null);
    setIsTermsContentLoading(false);
    setShippingNoticeAcknowledged(false);
    setCurrentStep(PRODUCT_SELECTION_STEP);
    onClose();
  };

  // When another tab locks SFH, settings refresh flips this flag — close any
  // in-progress wizard (pre-submit) and surface the locked modal.
  useEffect(() => {
    if (
      !isOpen ||
      !isSellFromHomeRestricted ||
      orderSubmittedRef.current ||
      closedForRestrictionRef.current
    ) {
      return;
    }

    closedForRestrictionRef.current = true;
    handleClose();
    setIsLockedModalOpen(true);
    // handleClose closes via parent onClose; intentionally omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSellFromHomeRestricted]);

  const handleHeaderBack = () => {
    if (currentStep === PRODUCT_SELECTION_STEP) {
      handleClose();
      return;
    }
    // The Shipping step may consume the back press internally (B2 -> B1).
    if (currentStep === SHIPPING_STEP && shippingBackRef.current?.()) {
      return;
    }
    goStepBack();
  };

  const handleSubmitOrder = (payload) => {
    orderSubmittedRef.current = true;
    refreshRecentTransactions();
    setLockedSnapshot(payload);
    goToStep(CONFIRMATION_STEP);
  };

  const handleViewOrder = () => goToStep(TRANSACTION_DETAILS_STEP);
  const handleViewHistory = () => goToStep(TRANSACTION_HISTORY_STEP);

  const showHeaderBack = (() => {
    if (
      currentStep === CONFIRMATION_STEP ||
      currentStep === TRANSACTION_DETAILS_STEP
    )
      return false;
    if (currentStep === PRODUCT_SELECTION_STEP) return Boolean(fromSellWizard);
    return true;
  })();

  const titleKey = STEP_TITLE_KEYS[currentStep];

  const handleTopBackClick = () => {
    sfhGtmEvent(SFH_TOP_BACK_CLICKED, { page_name: STEP_NAMES[currentStep] });
    handleHeaderBack();
  };

  const handleTopCloseClick = () => {
    sfhGtmEvent(SFH_TOP_CLOSE_CLICKED, { page_name: STEP_NAMES[currentStep] });
    handleClose();
  };

  return (
    <InstantSearch indexName={apiSfhIndex} searchClient={searchClient}>
      <Configure />
      <Modal
        isOpen={isOpen}
        close={handleTopCloseClick}
        goBack={showHeaderBack ? handleTopBackClick : undefined}
        title={titleKey ? <SfhModalTitle>{t(titleKey)}</SfhModalTitle> : ''}
        closeIconColor={themeColors.colorModalClose}
        headerBordered
        size="sm"
        maxWidth={SFH_WIDTH}
        minHeight={SFH_HEIGHT}
        height={STEP_FIXED_HEIGHTS[currentStep]}
        clipContent
        closeIconVisible
        hideHeader={currentStep === CONFIRMATION_STEP}
        skipListeningToLocations
      >
        {currentStep === PRODUCT_SELECTION_STEP && (
          <SellFromHomeProductSelection
            selectedHit={selectedHit}
            setSelectedHit={setSelectedHit}
            handleNext={goStepForward}
          />
        )}
        {currentStep === QUANTITY_STEP && (
          <SellFromHomeQuantity
            selectedHit={selectedHit}
            quantity={quantity}
            setQuantity={setQuantity}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            handleBack={goStepBack}
            handleNext={goStepForward}
            handleClose={handleClose}
            shippingNoticeAcknowledged={shippingNoticeAcknowledged}
            setShippingNoticeAcknowledged={setShippingNoticeAcknowledged}
          />
        )}
        {currentStep === SHIPPING_STEP && (
          <SellFromHomeShipping
            selectedHit={selectedHit}
            quantity={quantity}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedOriginAddress={setSelectedOriginAddress}
            setInitialQuote={setInitialQuote}
            setBackInterceptor={(fn) => {
              shippingBackRef.current = fn;
            }}
            handleNext={goStepForward}
          />
        )}
        {currentStep === REVIEW_STEP && (
          <SellFromHomeReview
            selectedHit={selectedHit}
            quantity={quantity}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            selectedOriginAddress={selectedOriginAddress}
            initialQuote={initialQuote}
            termsPdfUrl={termsPdfUrl}
            handleSubmitOrder={handleSubmitOrder}
            handleClose={handleClose}
          />
        )}
        {currentStep === CONFIRMATION_STEP && (
          <SellFromHomeConfirmation
            selectedHit={selectedHit}
            quantity={quantity}
            lockedUnitPrice={lockedSnapshot?.unitPrice ?? 0}
            lockedPayout={lockedSnapshot?.payout ?? 0}
            lockedSnapshot={lockedSnapshot}
            handleClose={handleClose}
            handleViewOrder={handleViewOrder}
          />
        )}
        {currentStep === TRANSACTION_DETAILS_STEP && (
          <SellFromHomeTransactionDetails
            selectedHit={selectedHit}
            quantity={quantity}
            lockedSnapshot={lockedSnapshot}
            handleClose={handleClose}
            handleViewHistory={handleViewHistory}
          />
        )}
        {currentStep === TRANSACTION_HISTORY_STEP && (
          <SellFromHomeTransactionHistory
            selectedHit={selectedHit}
            quantity={quantity}
            lockedSnapshot={lockedSnapshot}
            handleClose={handleClose}
          />
        )}
        <SellFromHomeStepsDrawer
          isOpen={isStepsDrawerOpen}
          onClose={() => setIsStepsDrawerOpen(false)}
        />
      </Modal>
      <SellFromHomeLockedModal
        isOpen={isLockedModalOpen}
        close={() => setIsLockedModalOpen(false)}
        title={t('irs.ssn.error.SSNTooManyAttempts.title')}
        message={t('irs.ssn.error.SSNTooManyAttempts.message')}
      />
    </InstantSearch>
  );
};

SellFromHomeWizard.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fromSellWizard: PropTypes.bool,
};

export default SellFromHomeWizard;
